import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '../auth'
import { usePrefs } from '../prefs'
import { useLang } from '../i18n'
import { STATUS_COLOR, getStatusColors, statusLabelKey } from './DutySchedulePage'

// ── Types ───────────────────────────────────────────────────────────────────

interface PersonalTask {
  id: string
  assignee_id: string
  category_code: string | null
  task_id: string | null
  title: string
  start_date: string
  original_start_date: string
  complete_date: string | null
  status: 'todo' | 'progress' | 'done'
  volume: number | null
  hours: number | null
  elapsed_seconds: number
  timer_state: 'idle' | 'running' | 'paused'
  timer_resumed_at: string | null
  assigned_by: string | null
  display_order: number
  completed_at: string | null
  created_at: string
  updated_at: string
  counting_rule: string | null
  assigned_by_name: string | null
}

interface DailySummaryDot { status: string }
interface ApiResponse {
  data: PersonalTask[]
  today_total_seconds: number
  daily_summary: Record<string, DailySummaryDot[]>
}

interface CategoryItem {
  id: string
  code: string
  display_name_cn: string
  display_name_en: string
  tasks: TaskDictItem[]
}

interface TaskDictItem {
  id: string
  category_code: string
  name: string
  counting_rule: string
  is_active: boolean
  deleted_at: string | null
}

interface MemberItem {
  id: string
  display_name: string
  role: string
  group_name: string
  is_active: boolean
  account_status: string
}

// ── Styles ──────────────────────────────────────────────────────────────────

const btnBase: React.CSSProperties = {
  padding: '4px 10px', border: '1px solid var(--border)', borderRadius: '4px',
  fontSize: '12px', cursor: 'pointer', background: 'var(--bg-surface)', color: 'var(--text-primary)',
}
const btnPrimary: React.CSSProperties = { ...btnBase, background: 'var(--accent)', borderColor: 'var(--accent)', color: 'var(--accent-contrast)' }
const btnDanger: React.CSSProperties = { ...btnBase, background: 'var(--danger)', borderColor: 'var(--danger)', color: 'var(--accent-contrast)' }
const btnGreen: React.CSSProperties = { ...btnBase, background: 'var(--success)', borderColor: 'var(--success)', color: 'var(--accent-contrast)' }
const btnGray: React.CSSProperties = { ...btnBase, background: 'var(--text-muted)', borderColor: 'var(--text-muted)', color: 'var(--accent-contrast)' }
const inputStyle: React.CSSProperties = {
  padding: '6px 8px', border: '1px solid var(--border)', borderRadius: '4px',
  fontSize: '13px', width: '100%', boxSizing: 'border-box',
  background: 'var(--bg-surface)', color: 'var(--text-primary)',
}
const selectStyle: React.CSSProperties = { ...inputStyle, background: 'var(--bg-surface)' }
const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'var(--overlay)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
}
const modalStyle: React.CSSProperties = {
  background: 'var(--bg-surface)', borderRadius: '8px', padding: '24px',
  width: '480px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 4px 24px rgba(0,0,0,0.25)', color: 'var(--text-primary)',
  border: '1px solid var(--border)',
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

async function apiFetch(path: string, opts: RequestInit = {}, token: string | null): Promise<any> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string> || {}),
  }
  if (token) headers['Authorization'] = `Bearer ${token}`
  const res = await fetch(path, { ...opts, headers })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err?.error?.message || `HTTP ${res.status}`)
  }
  if (res.status === 204) return {}
  return res.json()
}

function formatElapsed(elapsed_seconds: number, timerResumedAt: string | null): string {
  let totalSeconds = elapsed_seconds
  if (timerResumedAt) {
    totalSeconds += Math.floor((Date.now() - new Date(timerResumedAt).getTime()) / 1000)
  }
  totalSeconds = Math.max(0, Math.floor(totalSeconds))
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function statusColor(status: string): string {
  if (status === 'done') return 'var(--success)'
  if (status === 'progress') return 'var(--info)'
  return 'var(--text-muted)'
}

function statusLabel(status: string): string {
  if (status === 'done') return '已完成'
  if (status === 'progress') return '进行中'
  return '待开始'
}

function formatTotalSeconds(secs: number): string {
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  if (h > 0) return `${h}h${m > 0 ? m + 'm' : ''}`
  return `${m}m`
}

function monthStart(year: number, month: number): Date {
  return new Date(year, month, 1)
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate()
}

function isoDate(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

// Decompose elapsed seconds into [hours, minutes] with carry-over (minutes never > 59)
function secsToHM(secs: number): [number, number] {
  let h = Math.floor(secs / 3600)
  let m = Math.round((secs % 3600) / 60)
  if (m === 60) { h += 1; m = 0 }
  return [h, m]
}

// ── Component ────────────────────────────────────────────────────────────────

export function PersonalTasksPage() {
  const { user, accessToken } = useAuth()
  const { theme } = usePrefs()
  const { t } = useLang()
  const statusColors = getStatusColors(theme)
  const token = accessToken

  // selected member to view
  const [viewMemberId, setViewMemberId] = useState<string>(user?.id || '')
  const [members, setMembers] = useState<MemberItem[]>([])

  // calendar state
  const now = new Date()
  const [calYear, setCalYear] = useState(now.getFullYear())
  const [calMonth, setCalMonth] = useState(now.getMonth()) // 0-indexed
  const [selectedDate, setSelectedDate] = useState<string | null>(null)

  // tasks
  const [tasks, setTasks] = useState<PersonalTask[]>([])
  const [todayTotal, setTodayTotal] = useState(0)
  const [dailySummary, setDailySummary] = useState<Record<string, DailySummaryDot[]>>({})
  const [loading, setLoading] = useState(false)

  // categories
  const [categories, setCategories] = useState<CategoryItem[]>([])

  // new task modal
  const [showNewTask, setShowNewTask] = useState(false)
  const [newCategoryCode, setNewCategoryCode] = useState('')
  const [newTaskId, setNewTaskId] = useState('')
  const [newTitle, setNewTitle] = useState('')
  const [newStartDate, setNewStartDate] = useState(today())
  const [newError, setNewError] = useState('')
  const [newSubmitting, setNewSubmitting] = useState(false)

  // new task cross-category search
  const [newTaskSearch, setNewTaskSearch] = useState('')
  const [showSearchDropdown, setShowSearchDropdown] = useState(false)

  // delete modal
  const [deleteTask, setDeleteTask] = useState<PersonalTask | null>(null)
  const [deleteSubmitting, setDeleteSubmitting] = useState(false)

  // duty schedule for calendar overlay (read-only for member, T019 §14.8)
  const [scheduleMap, setScheduleMap] = useState<Record<string, string>>({}) // date → raw_status

  // drag state
  const dragId = useRef<string | null>(null)

  // live timer ticker
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(id)
  }, [])

  // beforeunload: warn when any timer is running (场景 4/4b)
  useEffect(() => {
    const hasRunning = tasks.some(t => t.timer_state === 'running')
    if (!hasRunning) return
    const handler = (e: BeforeUnloadEvent) => {
      const msg = '你的专注时钟还在滴答作响呢！建议先暂停。'
      e.preventDefault()
      e.returnValue = msg
      return msg
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [tasks])

  // load members for manager/admin
  useEffect(() => {
    if (!user || user.role === 'member') return
    apiFetch('/api/members', {}, token).then(res => {
      // /api/members returns {"members": [...]} (not {"data": [...]})
      const list: MemberItem[] = (res.members || []).filter(
        (m: MemberItem) => m.role !== 'admin' && m.is_active && m.account_status === 'active'
      )
      if (user.role === 'manager' && user.visible_group_scope && user.visible_group_scope.length > 0) {
        const scope = user.visible_group_scope
        setMembers(list.filter((m: MemberItem) => scope.includes(m.group_name)))
      } else {
        setMembers(list)
      }
    }).catch(() => {})
  }, [user, token])

  // For admin only: switch default view to first member since admin has no personal tasks.
  // Managers keep their own ID as default so they can always see their own assigned tasks.
  useEffect(() => {
    if (user?.role === 'admin' && members.length > 0 && viewMemberId === user?.id) {
      const inList = members.find(m => m.id === user?.id)
      if (!inList) setViewMemberId(members[0].id)
    }
  }, [members, user, viewMemberId])

  // load categories
  useEffect(() => {
    apiFetch('/api/categories', {}, token).then(res => setCategories(res.data || [])).catch(() => {})
  }, [token])

  // load tasks
  const loadTasks = useCallback(async () => {
    if (!viewMemberId) return
    setLoading(true)
    try {
      const params = new URLSearchParams({ assignee_id: viewMemberId })
      const res: ApiResponse = await apiFetch(`/api/personal-tasks?${params}`, {}, token)
      setTasks(res.data || [])
      setTodayTotal(res.today_total_seconds || 0)
      setDailySummary(res.daily_summary || {})
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }, [viewMemberId, token])

  useEffect(() => { loadTasks() }, [loadTasks])

  // fetch duty schedule for the currently displayed calendar month (T019 §14.8)
  useEffect(() => {
    const targetId = user?.role === 'member' ? user.id : viewMemberId
    if (!targetId) return
    const ym = `${calYear}${String(calMonth + 1).padStart(2, '0')}`
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {}
    fetch(`/api/duty-schedule?month=${ym}`, { headers })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => {
        const map: Record<string, string> = {}
        for (const cell of (d.data ?? [])) {
          // member sees own; manager/admin filtered by API already
          if (cell.member_id === targetId) {
            map[cell.date] = cell.raw_status
          }
        }
        setScheduleMap(map)
      })
      .catch(() => {})
  }, [calYear, calMonth, viewMemberId, user?.id, user?.role, token])

  // today's completed volume total (sum of volume, not count)
  const todayStr = today()
  const todayVolumeTotal = tasks
    .filter(t => t.status === 'done' && t.complete_date === todayStr)
    .reduce((sum, t) => sum + (t.volume || 0), 0)

  // tasks filtered by selected date — all-tasks mode sorts newest date first (场景8.1)
  const displayedTasks = selectedDate
    ? tasks.filter(t => t.start_date === selectedDate).sort((a, b) => a.display_order - b.display_order)
    : [...tasks].sort((a, b) => {
        if (a.start_date > b.start_date) return -1
        if (a.start_date < b.start_date) return 1
        return a.display_order - b.display_order
      })

  // group tasks by date when no day selected
  const groupedByDate: Record<string, PersonalTask[]> = {}
  if (!selectedDate) {
    for (const t of displayedTasks) {
      if (!groupedByDate[t.start_date]) groupedByDate[t.start_date] = []
      groupedByDate[t.start_date].push(t)
    }
  }

  // cross-category task search results (需求6)
  const allDictTasks = categories.flatMap(c =>
    c.tasks.filter(t => t.is_active && !t.deleted_at).map(t => ({ task: t, category: c }))
  )
  const searchResults = newTaskSearch.trim()
    ? allDictTasks.filter(({ task }) =>
        task.name.toLowerCase().includes(newTaskSearch.toLowerCase().trim())
      )
    : []

  function selectSearchTask(item: { task: TaskDictItem; category: CategoryItem }) {
    setNewCategoryCode(item.category.code)
    setNewTaskId(item.task.id)
    setNewTaskSearch('')
    setShowSearchDropdown(false)
    setNewError('')
  }

  // ── Timer actions ──────────────────────────────────────────────────────────

  async function timerAction(taskId: string, action: string, value?: number) {
    try {
      const body: any = { action }
      if (value !== undefined) body.value = value
      await apiFetch(`/api/personal-tasks/${taskId}/timer`, {
        method: 'POST', body: JSON.stringify(body),
      }, token)
      await loadTasks()
    } catch (e: any) {
      alert(e.message || '操作失败')
    }
  }

  // ── Complete (T009: called by TaskCard with validated volume + decimal hours) ──

  async function completeTask(taskId: string, volume: number, hours: number) {
    await apiFetch(`/api/personal-tasks/${taskId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ volume, hours }),
    }, token)
    await loadTasks()
  }

  async function submitUncomplete(task: PersonalTask) {
    try {
      await apiFetch(`/api/personal-tasks/${task.id}/uncomplete`, { method: 'POST', body: '{}' }, token)
      await loadTasks()
    } catch (e: any) {
      alert(e.message || '操作失败')
    }
  }

  // ── Delete ──────────────────────────────────────────────────────────────────

  async function submitDelete() {
    if (!deleteTask) return
    setDeleteSubmitting(true)
    try {
      await apiFetch(`/api/personal-tasks/${deleteTask.id}`, { method: 'DELETE' }, token)
      setDeleteTask(null)
      await loadTasks()
    } catch (e: any) {
      alert(e.message || '操作失败')
    } finally {
      setDeleteSubmitting(false)
    }
  }

  // ── New task ─────────────────────────────────────────────────────────────────

  const selectedCategory = categories.find(c => c.code === newCategoryCode)
  const isOthers = newCategoryCode === 'others'
  const availableTasks = selectedCategory ? selectedCategory.tasks.filter(t => t.is_active && !t.deleted_at) : []

  async function submitNewTask() {
    if (!newCategoryCode) { setNewError('请选择分类'); return }
    if (!isOthers && !newTaskId) { setNewError('请选择任务'); return }
    if (isOthers && !newTitle.trim()) { setNewError('请输入任务名称'); return }

    const taskItem = availableTasks.find(t => t.id === newTaskId)
    const finalTitle = isOthers ? newTitle.trim() : (taskItem?.name || '')

    setNewSubmitting(true)
    setNewError('')
    try {
      await apiFetch('/api/personal-tasks', {
        method: 'POST',
        body: JSON.stringify({
          assignee_id: viewMemberId,
          category_code: newCategoryCode,
          task_id: isOthers ? null : (newTaskId || null),
          title: finalTitle,
          start_date: newStartDate,
        }),
      }, token)
      setShowNewTask(false)
      setNewCategoryCode('')
      setNewTaskId('')
      setNewTitle('')
      setNewStartDate(today())
      setNewTaskSearch('')
      setShowSearchDropdown(false)
      await loadTasks()
    } catch (e: any) {
      setNewError(e.message || '操作失败')
    } finally {
      setNewSubmitting(false)
    }
  }

  // ── Drag reorder ──────────────────────────────────────────────────────────

  function handleDragStart(id: string) { dragId.current = id }
  function handleDragOver(e: React.DragEvent) { e.preventDefault() }
  async function handleDrop(targetId: string) {
    if (!dragId.current || dragId.current === targetId || !selectedDate) return
    const dateTaskIds = displayedTasks.map(t => t.id)
    const fromIdx = dateTaskIds.indexOf(dragId.current)
    const toIdx = dateTaskIds.indexOf(targetId)
    if (fromIdx < 0 || toIdx < 0) return
    const newOrder = [...dateTaskIds]
    newOrder.splice(fromIdx, 1)
    newOrder.splice(toIdx, 0, dragId.current)
    dragId.current = null
    try {
      await apiFetch('/api/personal-tasks/reorder', {
        method: 'POST',
        body: JSON.stringify({ date: selectedDate, assignee_id: viewMemberId, ordered_ids: newOrder }),
      }, token)
      await loadTasks()
    } catch { /* ignore */ }
  }

  // ── Calendar ──────────────────────────────────────────────────────────────

  const firstDay = monthStart(calYear, calMonth).getDay() // 0=Sun
  const numDays = daysInMonth(calYear, calMonth)

  function prevMonth() {
    if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11) }
    else setCalMonth(m => m - 1)
  }
  function nextMonth() {
    if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0) }
    else setCalMonth(m => m + 1)
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const canEdit = user?.role !== 'member' || viewMemberId === user?.id

  return (
    <div style={{ maxWidth: '960px', margin: '0 auto', color: 'var(--text-primary)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: '18px', color: 'var(--text-primary)' }}>个人任务</h2>

        {/* Member selector for manager/admin */}
        {user != null && user.role !== 'member' && members.length > 0 && (
          <select
            value={viewMemberId}
            onChange={e => setViewMemberId(e.target.value)}
            style={{ ...selectStyle, width: 'auto', minWidth: '140px' }}
          >
            {/* Always show "（我）" for manager/admin — ensures they can always view
                their own assigned tasks even when their group is outside their visible scope */}
            <option value={user.id}>{user.display_name}（我）</option>
            {members.filter(m => m.id !== user.id).map(m => (
              <option key={m.id} value={m.id}>{m.display_name}</option>
            ))}
          </select>
        )}

        {canEdit && (
          <button style={btnPrimary} onClick={() => { setShowNewTask(true); setNewError('') }}>
            + 新增任务
          </button>
        )}

        {/* Today total time — shown after 新增任务 button (场景7.1) */}
        <div style={{
          padding: '4px 12px', borderRadius: '12px',
          background: todayTotal > 28800 ? 'var(--danger-bg)' : 'var(--info-bg)',
          color: todayTotal > 28800 ? 'var(--danger)' : 'var(--info)',
          fontSize: '13px', fontWeight: 500,
        }}>
          今日工时：{formatTotalSeconds(todayTotal)}
          {todayTotal > 28800 && ' ⚠️'}
        </div>

        {/* Today's completed volume total (场景7.2) */}
        <div style={{
          padding: '4px 12px', borderRadius: '12px',
          background: 'var(--info-bg)', color: 'var(--info)',
          fontSize: '13px', fontWeight: 500,
        }}>
          今日工单量：{todayVolumeTotal}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Calendar */}
        <div style={{ minWidth: '280px', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px', background: 'var(--bg-surface)' }}>
          {/* Month nav */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <button style={btnBase} onClick={prevMonth}>‹</button>
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
              {calYear}年{calMonth + 1}月
            </span>
            <button style={btnBase} onClick={nextMonth}>›</button>
          </div>
          {/* Day headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '4px' }}>
            {['日', '一', '二', '三', '四', '五', '六'].map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: '11px', color: 'var(--text-muted)', padding: '2px' }}>{d}</div>
            ))}
          </div>
          {/* Day grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
            {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
            {Array.from({ length: numDays }).map((_, i) => {
              const day = i + 1
              const dateStr = isoDate(calYear, calMonth, day)
              const dots = (dailySummary[dateStr] || []).slice(0, 5)
              const isToday = dateStr === todayStr
              const isSelected = dateStr === selectedDate
              const schedStatus = scheduleMap[dateStr]
              const schedColor = schedStatus ? statusColors[schedStatus] : undefined
              const dayOfWeek = new Date(calYear, calMonth, day).getDay()
              const isWeekend = dayOfWeek === 0 || dayOfWeek === 6
              return (
                <div
                  key={dateStr}
                  onClick={() => setSelectedDate(isSelected ? null : dateStr)}
                  title={schedStatus ? `排班：${schedStatus}` : undefined}
                  style={{
                    padding: '4px 2px', textAlign: 'center', cursor: 'pointer', borderRadius: '4px',
                    border: isToday ? '1px solid var(--info)' : '1px solid transparent',
                    background: isSelected
                      ? 'var(--accent)'
                      : schedColor
                        ? `${schedColor}22`
                        : isWeekend
                          ? 'var(--danger-bg)'
                          : 'transparent',
                    color: isSelected ? 'var(--accent-contrast)' : (isToday ? 'var(--info)' : isWeekend ? 'var(--danger)' : 'var(--text-primary)'),
                    fontSize: '12px',
                    position: 'relative',
                  }}
                >
                  <div>{day}</div>
                  {/* Schedule status indicator bar */}
                  {schedColor && !isSelected && (
                    <div style={{
                      height: '3px',
                      background: schedColor,
                      borderRadius: '1px',
                      margin: '1px 2px 0',
                    }} />
                  )}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '2px', flexWrap: 'wrap', minHeight: '8px', marginTop: '2px' }}>
                    {dots.map((dot, di) => (
                      <div key={di} style={{
                        width: '6px', height: '6px', borderRadius: '50%',
                        background: isSelected ? 'rgba(255,255,255,0.8)' : statusColor(dot.status),
                      }} />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
          {/* Clear filter */}
          {selectedDate && (
            <button
              style={{ ...btnBase, width: '100%', marginTop: '8px', fontSize: '12px' }}
              onClick={() => setSelectedDate(null)}
            >
              显示全部任务
            </button>
          )}

          {/* Calendar legend — 13 duty schedule status colors (场景3.1-3.2) */}
          <div style={{ marginTop: '12px', borderTop: '1px solid var(--border)', paddingTop: '8px' }}>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: 500 }}>排班状态图例</div>
            {Object.keys(STATUS_COLOR).map(rs => (
              <div key={rs} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: statusColors[rs], flexShrink: 0 }} />
                <span style={{ fontSize: '11px', color: 'var(--text-primary)' }}>{t(statusLabelKey(rs))}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Task list */}
        <div style={{ flex: 1, minWidth: '300px' }}>
          {selectedDate && (
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 500 }}>
              {selectedDate} 的任务
              {displayedTasks.length === 0 && !loading && (
                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> — 暂无任务</span>
              )}
            </div>
          )}
          {loading && <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>加载中...</div>}

          {selectedDate ? (
            /* Day-filter mode: flat list with drag */
            <div>
              {displayedTasks.map(task => (
                <TaskCard
                  key={task.id + (task.status === 'done' ? '_done' : '')}
                  task={task}
                  canEdit={canEdit}
                  draggable={!!selectedDate}
                  onDragStart={() => handleDragStart(task.id)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(task.id)}
                  onTimer={timerAction}
                  onComplete={completeTask}
                  onUncomplete={() => submitUncomplete(task)}
                  onDelete={() => setDeleteTask(task)}
                />
              ))}
            </div>
          ) : (
            /* All-tasks mode: grouped by date */
            <div>
              {Object.keys(groupedByDate).sort((a, b) => b.localeCompare(a)).map(dateKey => (
                <div key={dateKey} style={{ marginBottom: '16px' }}>
                  <div style={{
                    fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)',
                    borderBottom: '1px solid var(--border)', paddingBottom: '4px', marginBottom: '6px',
                  }}>
                    {dateKey}
                    {dateKey === todayStr && (
                      <span style={{ marginLeft: '6px', color: 'var(--info)', fontWeight: 400 }}>今天</span>
                    )}
                  </div>
                  {groupedByDate[dateKey].map(task => (
                    <TaskCard
                      key={task.id + (task.status === 'done' ? '_done' : '')}
                      task={task}
                      canEdit={canEdit}
                      draggable={false}
                      onDragStart={() => {}}
                      onDragOver={() => {}}
                      onDrop={() => {}}
                      onTimer={timerAction}
                      onComplete={completeTask}
                      onUncomplete={() => submitUncomplete(task)}
                      onDelete={() => setDeleteTask(task)}
                    />
                  ))}
                </div>
              ))}
              {Object.keys(groupedByDate).length === 0 && !loading && (
                <div style={{ color: 'var(--text-muted)', fontSize: '13px', padding: '24px 0' }}>暂无任务</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* New task modal */}
      {showNewTask && (
        <div style={overlayStyle} onClick={() => setShowNewTask(false)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: '15px', color: 'var(--text-primary)' }}>新增任务</h3>

            {/* Cross-category task name search (场景6.1-6.3) */}
            <div style={{ marginBottom: '10px', position: 'relative' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>按任务名搜索（可不选分类）</label>
              <input
                style={inputStyle}
                value={newTaskSearch}
                onChange={e => { setNewTaskSearch(e.target.value); setShowSearchDropdown(true) }}
                onFocus={() => { if (newTaskSearch.trim()) setShowSearchDropdown(true) }}
                onBlur={() => setTimeout(() => setShowSearchDropdown(false), 150)}
                placeholder="输入任务名关键字..."
              />
              {showSearchDropdown && newTaskSearch.trim().length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                  background: 'var(--bg-surface)', border: '1px solid var(--border)',
                  borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  maxHeight: '200px', overflowY: 'auto',
                }}>
                  {searchResults.length > 0
                    ? searchResults.slice(0, 10).map(({ task, category }) => (
                        <div
                          key={task.id}
                          onMouseDown={() => selectSearchTask({ task, category })}
                          style={{
                            padding: '8px 10px', cursor: 'pointer', fontSize: '13px',
                            borderBottom: '1px solid var(--border)',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          }}
                          onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-elevated)' }}
                          onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = '' }}
                        >
                          <span>{task.name}</span>
                          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{category.display_name_en}</span>
                        </div>
                      ))
                    : (
                        <div style={{ padding: '12px 10px', fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center' }}>
                          无匹配任务
                        </div>
                      )
                  }
                </div>
              )}
            </div>

            <div style={{ marginBottom: '10px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>分类</label>
              <select style={selectStyle} value={newCategoryCode} onChange={e => { setNewCategoryCode(e.target.value); setNewTaskId(''); setNewTaskSearch('') }}>
                <option value="">请选择分类</option>
                {categories.map(c => (
                  <option key={c.code} value={c.code}>{c.display_name_en}</option>
                ))}
              </select>
            </div>
            {newCategoryCode && !isOthers && (
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>任务</label>
                <select style={selectStyle} value={newTaskId} onChange={e => setNewTaskId(e.target.value)}>
                  <option value="">请选择任务</option>
                  {availableTasks.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {newTaskId && availableTasks.find(t => t.id === newTaskId)?.counting_rule && (
                  <div style={{ fontSize: '11px', color: 'var(--info)', marginTop: '3px' }}>
                    计数规则：{availableTasks.find(t => t.id === newTaskId)?.counting_rule}
                  </div>
                )}
              </div>
            )}
            {isOthers && (
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>任务名称</label>
                <input style={inputStyle} value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="自定义任务名称" />
              </div>
            )}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>开始日期</label>
              <input type="date" style={inputStyle} value={newStartDate} onChange={e => setNewStartDate(e.target.value)} />
            </div>
            {newError && <div style={{ color: 'var(--danger)', fontSize: '12px', marginBottom: '10px' }}>{newError}</div>}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button style={btnBase} onClick={() => setShowNewTask(false)}>取消</button>
              <button style={btnPrimary} onClick={submitNewTask} disabled={newSubmitting}>
                {newSubmitting ? '提交中...' : '添加'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTask && (
        <div style={overlayStyle} onClick={() => setDeleteTask(null)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: '15px', color: 'var(--text-primary)' }}>删除任务</h3>
            <div style={{ fontSize: '13px', marginBottom: '8px', color: 'var(--text-primary)' }}>
              <strong>{deleteTask.title}</strong>
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
              状态：{statusLabel(deleteTask.status)} · 已计时：{formatElapsed(deleteTask.elapsed_seconds, null)}
            </div>
            {(deleteTask.timer_state !== 'idle' || deleteTask.status === 'done') && (
              <div style={{ color: 'var(--danger)', fontSize: '12px', marginBottom: '10px' }}>
                {deleteTask.timer_state === 'running' && '⚠️ 计时器正在运行，删除将中止计时。'}
                {deleteTask.timer_state === 'paused' && '⚠️ 计时器已暂停，删除将丢失计时数据。'}
                {deleteTask.status === 'done' && '⚠️ 此任务已完成，删除将同步删除关联工作记录。'}
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button style={btnBase} onClick={() => setDeleteTask(null)}>取消</button>
              <button style={btnDanger} onClick={submitDelete} disabled={deleteSubmitting}>
                {deleteSubmitting ? '删除中...' : '确认删除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── TaskCard ─────────────────────────────────────────────────────────────────

interface TaskCardProps {
  task: PersonalTask
  canEdit: boolean
  draggable: boolean
  onDragStart: () => void
  onDragOver: (e: React.DragEvent) => void
  onDrop: () => void
  onTimer: (taskId: string, action: string, value?: number) => Promise<void>
  onComplete: (taskId: string, volume: number, hours: number) => Promise<void>
  onUncomplete: () => void
  onDelete: () => void
}

function TaskCard({
  task, canEdit, draggable,
  onDragStart, onDragOver, onDrop,
  onTimer, onComplete, onUncomplete, onDelete,
}: TaskCardProps) {
  const isRunning = task.timer_state === 'running'
  const isPaused = task.timer_state === 'paused'
  const isDone = task.status === 'done'
  const isAssigned = !!task.assigned_by

  // T009: always-on inline complete form state (non-done tasks only).
  // Lazy-initialized from task.hours (after uncomplete) or elapsed_seconds (timer auto-fill).
  // The parent uses key={taskId + (done?'_done':'')} so this component remounts when the
  // task transitions done→active, ensuring preserved volume/hours are picked up fresh.
  const [volume, setVolume] = useState(() =>
    task.volume != null ? String(task.volume) : ''
  )
  const [hoursH, setHoursH] = useState(() => {
    if (task.hours != null && task.hours > 0) return String(Math.floor(task.hours))
    let secs = task.elapsed_seconds
    if (task.timer_state === 'running' && task.timer_resumed_at) {
      secs += Math.floor((Date.now() - new Date(task.timer_resumed_at).getTime()) / 1000)
    }
    const [h] = secsToHM(secs)
    return String(h)
  })
  const [hoursM, setHoursM] = useState(() => {
    if (task.hours != null && task.hours > 0) {
      const h = Math.floor(task.hours)
      return String(Math.round((task.hours - h) * 60))
    }
    let secs = task.elapsed_seconds
    if (task.timer_state === 'running' && task.timer_resumed_at) {
      secs += Math.floor((Date.now() - new Date(task.timer_resumed_at).getTime()) / 1000)
    }
    const [, m] = secsToHM(secs)
    return String(m)
  })
  const [inlineError, setInlineError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  async function handleComplete() {
    // Validate volume
    if (volume === '') { setInlineError('处理量须填写'); return }
    const volNum = Number(volume)
    if (!Number.isInteger(volNum) || volNum < 0) { setInlineError('处理量须为整数且 ≥0'); return }
    // Validate hours and minutes
    const hNum = Number(hoursH)
    const mNum = Number(hoursM)
    if (!Number.isInteger(hNum) || hNum < 0) { setInlineError('小时须为整数且 ≥0'); return }
    if (!Number.isInteger(mNum) || mNum < 0 || mNum > 59) { setInlineError('分钟须为 0–59 的整数'); return }
    // Work time > 0: hours and minutes cannot both be 0
    if (hNum === 0 && mNum === 0) { setInlineError('工时不得为 0（小时与分钟不可同时为 0）'); return }
    // Fold into decimal hours with 2 decimal places (§2.6.1)
    const hoursDecimal = parseFloat((hNum + mNum / 60).toFixed(2))
    setInlineError('')
    setSubmitting(true)
    try {
      await onComplete(task.id, volNum, hoursDecimal)
    } catch (e: any) {
      setInlineError(e.message || '操作失败')
    } finally {
      setSubmitting(false)
    }
  }

  const cardStyle: React.CSSProperties = {
    border: '1px solid var(--border)', borderRadius: '6px', padding: '10px 12px',
    marginBottom: '8px',
    background: isDone ? 'var(--success-bg)' : 'var(--bg-surface)',
    borderLeft: `3px solid ${statusColor(task.status)}`,
    cursor: draggable ? 'grab' : 'default',
    opacity: isDone ? 0.85 : 1,
  }

  const compactInput: React.CSSProperties = {
    padding: '3px 5px', border: '1px solid var(--border)', borderRadius: '4px',
    fontSize: '12px', background: 'var(--bg-surface)', color: 'var(--text-primary)',
  }

  return (
    <div
      style={cardStyle}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        {/* Done indicator */}
        {canEdit && (
          <div
            style={{
              width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0, marginTop: '2px',
              border: `2px solid ${statusColor(task.status)}`,
              background: isDone ? statusColor(task.status) : 'transparent',
              cursor: 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {isDone && <span style={{ color: 'var(--accent-contrast)', fontSize: '10px', lineHeight: 1 }}>✓</span>}
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span style={{
              fontSize: '13px', fontWeight: 500,
              textDecoration: isDone ? 'line-through' : 'none',
              color: isDone ? 'var(--text-muted)' : 'var(--text-primary)',
            }}>
              {task.title}
            </span>
            <span style={{
              fontSize: '11px', padding: '1px 6px', borderRadius: '10px',
              background: isDone ? 'var(--success-bg)' : (isRunning ? 'var(--info-bg)' : 'var(--bg-elevated)'),
              color: isDone ? 'var(--success)' : (isRunning ? 'var(--info)' : 'var(--text-muted)'),
            }}>
              {statusLabel(task.status)}
            </span>
            {isAssigned && (
              <span style={{ fontSize: '11px', color: 'var(--warning)' }} title={`由 ${task.assigned_by_name || '他人'} 分派`}>
                🎯 Assigned
              </span>
            )}
          </div>

          {/* Category/counting_rule hint */}
          {task.counting_rule && (
            <div style={{ fontSize: '11px', color: 'var(--info)', marginTop: '2px' }}>
              计数规则：{task.counting_rule}
            </div>
          )}

          {/* T009: always-on completion form — constant for non-done tasks (场景9.1) */}
          {canEdit && !isDone && (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
              <div>
                <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>处理量</label>
                <input
                  type="number" min="0"
                  style={{ ...compactInput, width: '64px' }}
                  value={volume}
                  onChange={e => setVolume(e.target.value)}
                  placeholder="量"
                />
              </div>
              <div>
                <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>小时</label>
                <input
                  type="number" min="0"
                  style={{ ...compactInput, width: '52px' }}
                  value={hoursH}
                  onChange={e => setHoursH(e.target.value)}
                  placeholder="h"
                />
              </div>
              <div>
                <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>分钟</label>
                <input
                  type="number" min="0" max="59"
                  style={{ ...compactInput, width: '52px' }}
                  value={hoursM}
                  onChange={e => setHoursM(e.target.value)}
                  placeholder="m"
                />
              </div>
              <button
                style={{ ...btnGreen, padding: '3px 10px', fontSize: '12px' }}
                onClick={handleComplete}
                disabled={submitting}
              >
                {submitting ? '...' : '完成'}
              </button>
            </div>
          )}

          {/* Validation error for completion form */}
          {canEdit && !isDone && inlineError && (
            <div style={{ color: 'var(--danger)', fontSize: '11px', marginTop: '3px' }}>{inlineError}</div>
          )}

          {/* Timer row */}
          {canEdit && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
              {/* Timer display — read-only, no click-to-edit */}
              <span style={{ fontSize: '13px', fontFamily: 'monospace', color: isRunning ? 'var(--info)' : 'var(--text-primary)' }}>
                {formatElapsed(task.elapsed_seconds, isRunning ? task.timer_resumed_at : null)}
              </span>

              {/* Reset only (no +1m/-1m) */}
              <button style={{ ...btnBase, padding: '1px 6px', fontSize: '11px' }}
                onClick={() => onTimer(task.id, 'reset')}>Reset</button>

              {/* Timer control buttons */}
              {!isDone && (
                <>
                  {task.timer_state === 'idle' && (
                    <button style={{ ...btnPrimary, padding: '2px 8px', fontSize: '11px' }}
                      onClick={() => onTimer(task.id, 'start')}>▶ Start</button>
                  )}
                  {isRunning && (
                    <button style={{ ...btnBase, padding: '2px 8px', fontSize: '11px', borderColor: 'var(--warning)', color: 'var(--warning)' }}
                      onClick={() => onTimer(task.id, 'pause')}>⏸ Pause</button>
                  )}
                  {isPaused && (
                    <button style={{ ...btnPrimary, padding: '2px 8px', fontSize: '11px' }}
                      onClick={() => onTimer(task.id, 'resume')}>▶ Resume</button>
                  )}
                  {(isRunning || isPaused) && (
                    <button style={{ ...btnBase, padding: '2px 8px', fontSize: '11px', borderColor: 'var(--text-muted)', color: 'var(--text-muted)' }}
                      onClick={() => onTimer(task.id, 'stop')}>■ Stop</button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Action buttons — only uncomplete/delete (no separate 完成 button: it's in the form) */}
        <div style={{ display: 'flex', gap: '4px', flexShrink: 0, flexDirection: 'column', alignItems: 'flex-end' }}>
          {canEdit && isDone && (
            <button style={{ ...btnGray, padding: '2px 8px', fontSize: '11px' }} onClick={onUncomplete}>
              撤销完成
            </button>
          )}
          {canEdit && !isAssigned && (
            <button
              style={{ ...btnDanger, padding: '6px 12px', fontSize: '16px' }}
              title="删除任务"
              onClick={onDelete}
            >
              🗑
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
